import type {ReactNode, Ref} from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowRight, Check, Mail} from 'lucide-react';
import {Button, cn} from '@chekinapp/ui';
import {useStaySummary} from 'hooks/useStaySummary';
import {useGuestGroup} from 'hooks/useGuestGroup';
import {WEEKDAY_DATE_OPTIONS, formatClockLabel, formatDisplayDate} from 'utils/dates';
import {GoogleSignInButton} from 'pages/Auth/components/SocialLoginButtons';
import {PropertyLinkShell} from 'pages/SearchReservation/components/PropertyLinkShell';
import {StepHeading} from './StepParts';
import styles from '../instantCheckIn.module.css';

const SIGN_IN_BUTTON_CLASSES =
  'h-[46px] w-full gap-2.5 rounded-xl border-0 bg-[var(--chekin-color-white)] text-[15px] font-semibold text-[var(--chekin-color-brand-navy)] hover:bg-[var(--chekin-color-white)] desk:border-0';

type NodeProps = {
  state: 'done' | 'now' | 'next';
  icon?: ReactNode;
  title: string;
  detail?: string;
  children?: ReactNode;
};

function TimelineNode({state, icon, title, detail, children}: NodeProps) {
  return (
    <li
      className={cn(styles.node, styles[state])}
      aria-current={state === 'now' ? 'step' : undefined}
    >
      <span className={styles.dot} aria-hidden>
        {icon}
      </span>
      <div className="min-w-0 pt-[3px]">
        <b className="block text-[15px] font-semibold leading-5 text-[var(--chekin-color-brand-navy)]">
          {title}
        </b>
        {detail && (
          <span className="mt-0.5 block text-[13px] leading-[18px] text-[color:var(--chekin-text-secondary)]">
            {detail}
          </span>
        )}
        {children}
      </div>
    </li>
  );
}

type Props = {
  columnRef: Ref<HTMLDivElement>;
  onSignedIn: () => void;
  onEmail: () => void;
  onSkip: () => void;
};

function WelcomeStep({columnRef, onSignedIn, onEmail, onSkip}: Props) {
  const {t, i18n} = useTranslation();
  const stay = useStaySummary();
  const {totalGuests, totalRegisteredGuests} = useGuestGroup();
  const guestCount = totalGuests || stay.guests || 0;
  const isEveryoneCheckedIn = guestCount > 0 && totalRegisteredGuests >= guestCount;

  const checkIn = formatDisplayDate(
    stay.checkInDate,
    i18n.language,
    WEEKDAY_DATE_OPTIONS,
  );
  const checkOut = formatDisplayDate(
    stay.checkOutDate,
    i18n.language,
    WEEKDAY_DATE_OPTIONS,
  );
  const checkInTime = formatClockLabel(stay.checkInTime, i18n.language);
  const checkOutTime = formatClockLabel(stay.checkOutTime, i18n.language);

  const arrival = [
    checkIn,
    checkInTime && t('instant_check_in.from_time', {time: checkInTime}),
  ]
    .filter(Boolean)
    .join(' · ');
  const departure = [
    checkOut,
    checkOutTime && t('instant_check_in.by_time', {time: checkOutTime}),
    stay.nights && t('nights_count', {count: stay.nights}),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <PropertyLinkShell columnRef={columnRef}>
      <StepHeading>{t('instant_check_in.your_stay')}</StepHeading>

      <ol className={styles.timeline}>
        <TimelineNode
          state="done"
          icon={<Check size={14} strokeWidth={3} />}
          title={t('instant_check_in.booked')}
          detail={stay.bookingRef || t('instant_check_in.confirmed')}
        />
        <TimelineNode
          state="now"
          title={t('instant_check_in.online_check_in')}
          detail={t('instant_check_in.required_before_arrival')}
        >
          <div
            className={cn(
              styles.signInPanel,
              'mt-2.5 flex flex-col gap-2.5 rounded-[18px] p-3',
            )}
          >
            <p className="mb-0.5 text-[13px] leading-[1.45] text-[color:var(--chekin-text-secondary)]">
              <b className="block text-[14px] font-semibold text-[var(--chekin-color-brand-navy)]">
                {t('instant_check_in.checked_in_progress', {
                  done: totalRegisteredGuests,
                  count: guestCount,
                })}
              </b>
              {isEveryoneCheckedIn ? (
                t('instant_check_in.save_guests_for_next_time')
              ) : (
                <>
                  {t('instant_check_in.do_it_before_arrival')}{' '}
                  <span className="text-[var(--chekin-color-brand-navy)]">
                    {t('instant_check_in.sign_up_tip')}
                  </span>
                </>
              )}
            </p>
            <GoogleSignInButton
              onSuccessAuth={onSignedIn}
              className={SIGN_IN_BUTTON_CLASSES}
            />
            <Button
              variant="outline"
              onClick={onEmail}
              className={SIGN_IN_BUTTON_CLASSES}
            >
              <Mail size={18} className="shrink-0" />
              {t('instant_check_in.continue_with_email')}
            </Button>
            <p className="mx-0.5 mt-0.5 text-[12px] italic leading-[1.4] text-[color:var(--chekin-text-secondary)]">
              {t('instant_check_in.been_here_before')}
            </p>
            <Button
              variant="link"
              onClick={onSkip}
              className="h-9 gap-1.5 self-center p-0 text-[14px] font-bold"
            >
              {t('instant_check_in.continue_without_account')}
              <ArrowRight size={16} />
            </Button>
          </div>
        </TimelineNode>
        <TimelineNode
          state="next"
          title={t('instant_check_in.arrival')}
          detail={arrival}
        />
        <TimelineNode
          state="next"
          title={t('instant_check_in.departure')}
          detail={departure}
        />
      </ol>
    </PropertyLinkShell>
  );
}

export {WelcomeStep};
